import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Badge,
  Snackbar,
  Alert,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as PendingIcon,
  Assignment as OrderIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Store as StoreIcon,
  QrCode as QrCodeIcon,
  Verified as VerifiedIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import QRCode from 'qrcode';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color, bgcolor;
  
  switch (status) {
    case 'pending':
      color = '#ed6c02';
      bgcolor = '#fff3e0';
      break;
    case 'confirmed':
      color = '#2e7d32';
      bgcolor = '#e8f5e9';
      break;
    case 'processing':
      color = '#1976d2';
      bgcolor = '#e3f2fd';
      break;
    case 'shipped':
      color = '#9c27b0';
      bgcolor = '#f3e5f5';
      break;
    case 'delivered':
      color = '#388e3c';
      bgcolor = '#e8f5e9';
      break;
    case 'cancelled':
      color = '#d32f2f';
      bgcolor = '#ffebee';
      break;
    default:
      color = theme.palette.text.secondary;
      bgcolor = theme.palette.grey[100];
  }
  
  return {
    color,
    backgroundColor: bgcolor,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color
    }
  };
});

// Enhanced Invoice Component
const InvoiceTemplate = ({ order, onClose }) => {
  const printRef = useRef();
  const [qrCodeData, setQrCodeData] = useState('');

  useEffect(() => {
    // Generate QR Code with order information
    const orderInfo = {
      orderId: order.id,
      customer: order.customerName,
      total: order.totalAmount,
      date: order.orderDate,
      store: 'গ্রামীণ বাজার',
      url: `https://grameenbazar.com/order/${order.id}`
    };

    QRCode.toDataURL(JSON.stringify(orderInfo), {
      width: 150,
      margin: 2,
      color: {
        dark: '#1976d2',
        light: '#FFFFFF'
      }
    }).then(url => {
      setQrCodeData(url);
    }).catch(err => {
      console.error('QR Code generation error:', err);
    });
  }, [order]);

  const handlePrint = () => {
    const printContent = printRef.current;
    const winPrint = window.open('', '', 'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0');
    
    winPrint.document.write(`
      <html>
        <head>
          <title>ইনভয়েস - ${order.id}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body { 
              font-family: 'Inter', Arial, sans-serif; 
              font-size: 13px;
              line-height: 1.4;
              color: #333;
              background: #f5f5f5;
              padding: 20px;
            }
            
            .invoice-container {
              max-width: 794px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
            }
            
            .invoice-header {
              background: #4ade80;
              color: white;
              padding: 30px 40px;
              font-size: 28px;
              font-weight: 600;
              text-align: center;
              letter-spacing: 2px;
            }
            
            .invoice-content {
              padding: 40px;
            }
            
            .invoice-meta {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 40px;
              margin-bottom: 60px;
            }
            
            .invoice-number {
              text-align: center;
            }
            
            .invoice-number .label {
              font-size: 11px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-bottom: 5px;
            }
            
            .invoice-number .value {
              font-size: 16px;
              font-weight: 600;
              color: #333;
            }
            
            .invoice-date {
              text-align: center;
            }
            
            .invoice-date .label {
              font-size: 11px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-bottom: 5px;
            }
            
            .invoice-date .value {
              font-size: 14px;
              color: #333;
            }
            
            .qr-section {
              text-align: center;
            }
            
            .qr-code {
              width: 80px;
              height: 80px;
              margin: 0 auto;
            }
            
            .company-client-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 60px;
              margin-bottom: 50px;
            }
            
            .section-title {
              font-size: 11px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin-bottom: 15px;
              font-weight: 500;
            }
            
            .company-info .company-name {
              font-size: 16px;
              font-weight: 600;
              color: #333;
              margin-bottom: 5px;
            }
            
            .company-info .company-details,
            .client-info .client-details {
              font-size: 13px;
              color: #666;
              line-height: 1.6;
            }
            
            .client-info .client-name {
              font-size: 15px;
              font-weight: 600;
              color: #333;
              margin-bottom: 5px;
            }
            
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }
            
            .items-table thead {
              background: #f8f9fa;
            }
            
            .items-table th {
              padding: 15px 10px;
              text-align: left;
              font-size: 11px;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-weight: 500;
              border-bottom: 1px solid #e5e7eb;
            }
            
            .items-table th.text-center {
              text-align: center;
            }
            
            .items-table th.text-right {
              text-align: right;
            }
            
            .items-table td {
              padding: 15px 10px;
              border-bottom: 1px solid #f1f5f9;
              font-size: 13px;
              color: #333;
            }
            
            .items-table tbody tr:hover {
              background: #fafafa;
            }
            
            .item-name {
              font-weight: 500;
            }
            
            .text-center {
              text-align: center;
            }
            
            .text-right {
              text-align: right;
            }
            
            .totals-section {
              margin-top: 40px;
              display: flex;
              justify-content: flex-end;
            }
            
            .totals-table {
              width: 300px;
            }
            
            .totals-table td {
              padding: 8px 0;
              border: none;
              font-size: 13px;
            }
            
            .totals-table .subtotal-row td {
              color: #666;
              border-bottom: 1px solid #f1f5f9;
            }
            
            .totals-table .total-row td {
              font-weight: 600;
              font-size: 16px;
              color: #333;
              border-top: 2px solid #4ade80;
              padding-top: 15px;
            }
            
            .payment-terms {
              margin-top: 60px;
              padding: 25px;
              background: #f8f9fa;
              border-left: 4px solid #4ade80;
            }
            
            .payment-terms .title {
              font-size: 14px;
              font-weight: 600;
              color: #333;
              margin-bottom: 10px;
            }
            
            .payment-terms .content {
              font-size: 12px;
              color: #666;
              line-height: 1.6;
            }
            
            .footer-section {
              margin-top: 80px;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
            
            .signature-area {
              margin-top: 60px;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 100px;
            }
            
            .signature-box {
              text-align: center;
            }
            
            .signature-line {
              border-top: 1px solid #ccc;
              margin-top: 60px;
              padding-top: 8px;
              font-size: 12px;
              color: #666;
            }
            
            @media print {
              body {
                background: white;
                padding: 0;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              
              .invoice-container {
                box-shadow: none;
                margin: 0;
                max-width: 100%;
              }
              
              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    winPrint.document.close();
    winPrint.focus();
    winPrint.print();
    winPrint.close();
  };

  const calculateSubtotal = () => {
    return order.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };

  const deliveryCharge = 50;
  const subtotal = calculateSubtotal();
  const total = subtotal + deliveryCharge;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableScrollLock={true}
      PaperProps={{
        sx: { minHeight: '90vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6">ইনভয়েস</Typography>
            <Chip 
              icon={<VerifiedIcon />} 
              label="প্রিমিয়াম" 
              color="primary" 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Box>
          <Box className="no-print">
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ 
                mr: 1,
                background: '#4ade80',
                '&:hover': {
                  background: '#22c55e',
                }
              }}
            >
              প্রিন্ট করুন
            </Button>
            <Button onClick={onClose}>বন্ধ</Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: 0,
          '& .invoice-container': {
            fontFamily: '"Inter", Arial, sans-serif',
            fontSize: '13px',
            lineHeight: 1.4,
            color: '#333'
          },
          '& .invoice-header': {
            background: '#4ade80',
            color: 'white',
            padding: '30px 40px',
            fontSize: '28px',
            fontWeight: 600,
            textAlign: 'center',
            letterSpacing: '2px'
          },
          '& .invoice-content': {
            padding: '40px'
          },
          '& .invoice-meta': {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '40px',
            marginBottom: '60px'
          },
          '& .invoice-number, & .invoice-date, & .qr-section': {
            textAlign: 'center'
          },
          '& .invoice-number .label, & .invoice-date .label': {
            fontSize: '11px',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '5px'
          },
          '& .invoice-number .value': {
            fontSize: '16px',
            fontWeight: 600,
            color: '#333'
          },
          '& .invoice-date .value': {
            fontSize: '14px',
            color: '#333'
          },
          '& .qr-code': {
            width: '80px',
            height: '80px',
            margin: '0 auto'
          },
          '& .company-client-section': {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '60px',
            marginBottom: '50px'
          },
          '& .section-title': {
            fontSize: '11px',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '15px',
            fontWeight: 500
          },
          '& .company-info .company-name': {
            fontSize: '16px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '5px'
          },
          '& .company-info .company-details, & .client-info .client-details': {
            fontSize: '13px',
            color: '#666',
            lineHeight: 1.6
          },
          '& .client-info .client-name': {
            fontSize: '15px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '5px'
          },
          '& .items-table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '30px'
          },
          '& .items-table thead': {
            background: '#f8f9fa'
          },
          '& .items-table th': {
            padding: '15px 10px',
            textAlign: 'left',
            fontSize: '11px',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: 500,
            borderBottom: '1px solid #e5e7eb'
          },
          '& .items-table th.text-center': {
            textAlign: 'center'
          },
          '& .items-table th.text-right': {
            textAlign: 'right'
          },
          '& .items-table td': {
            padding: '15px 10px',
            borderBottom: '1px solid #f1f5f9',
            fontSize: '13px',
            color: '#333'
          },
          '& .items-table tbody tr:hover': {
            background: '#fafafa'
          },
          '& .item-name': {
            fontWeight: 500
          },
          '& .text-center': {
            textAlign: 'center'
          },
          '& .text-right': {
            textAlign: 'right'
          },
          '& .totals-section': {
            marginTop: '40px',
            display: 'flex',
            justifyContent: 'flex-end'
          },
          '& .totals-table': {
            width: '300px'
          },
          '& .totals-table td': {
            padding: '8px 0',
            border: 'none',
            fontSize: '13px'
          },
          '& .totals-table .subtotal-row td': {
            color: '#666',
            borderBottom: '1px solid #f1f5f9'
          },
          '& .totals-table .total-row td': {
            fontWeight: 600,
            fontSize: '16px',
            color: '#333',
            borderTop: '2px solid #4ade80',
            paddingTop: '15px'
          },
          '& .payment-terms': {
            marginTop: '60px',
            padding: '25px',
            background: '#f8f9fa',
            borderLeft: '4px solid #4ade80'
          },
          '& .payment-terms .title': {
            fontSize: '14px',
            fontWeight: 600,
            color: '#333',
            marginBottom: '10px'
          },
          '& .payment-terms .content': {
            fontSize: '12px',
            color: '#666',
            lineHeight: 1.6
          },
          '& .signature-area': {
            marginTop: '60px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '100px'
          },
          '& .signature-box': {
            textAlign: 'center'
          },
          '& .signature-line': {
            borderTop: '1px solid #ccc',
            marginTop: '60px',
            paddingTop: '8px',
            fontSize: '12px',
            color: '#666'
          },
          '& .footer-section': {
            marginTop: '80px',
            textAlign: 'center',
            color: '#666',
            fontSize: '12px'
          }
        }}
      >
        <div ref={printRef}>
          <div className="invoice-container">
            {/* Clean Header */}
            <div className="invoice-header">
              ইনভয়েস
            </div>

            <div className="invoice-content">
              {/* Invoice Meta Information */}
              <div className="invoice-meta">
                <div className="invoice-number">
                  <div className="label">ইনভয়েস নম্বর</div>
                  <div className="value">{order.id}</div>
                </div>
                
                <div className="invoice-date">
                  <div className="label">তারিখ</div>
                  <div className="value">{order.orderDate}</div>
                </div>
                
                {qrCodeData && (
                  <div className="qr-section">
                    <img src={qrCodeData} alt="QR Code" className="qr-code" />
                  </div>
                )}
              </div>

              {/* Company and Client Information */}
              <div className="company-client-section">
                <div className="company-info">
                  <div className="section-title">বিক্রেতা</div>
                  <div className="company-name">গ্রামীণ বাজার</div>
                  <div className="company-details">
                    ১২ মেইন রোড, ঢাকা ১২০০<br />
                    ফোন: ০১৭১২৩৪৫৬৭২<br />
                    ইমেইল: info@grameenbazar.com<br />
                    ওয়েবসাইট: www.grameenbazar.com
                  </div>
                </div>

                <div className="client-info">
                  <div className="section-title">ক্রেতা</div>
                  <div className="client-name">{order.customerName}</div>
                  <div className="client-details">
                    {order.customerAddress}<br />
                    ফোন: {order.customerPhone}<br />
                    পেমেন্ট: {order.paymentMethod}
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <table className="items-table">
                <thead>
                  <tr>
                    <th>পণ্য</th>
                    <th className="text-center">পরিমাণ</th>
                    <th className="text-right">একক দাম</th>
                    <th className="text-right">মোট</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, index) => (
                    <tr key={index}>
                      <td className="item-name">{item.name}</td>
                      <td className="text-center">{item.quantity} {item.unit}</td>
                      <td className="text-right">৳{item.price.toLocaleString()}</td>
                      <td className="text-right">৳{(item.quantity * item.price).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals Section */}
              <div className="totals-section">
                <table className="totals-table">
                  <tbody>
                    <tr className="subtotal-row">
                      <td>সাবটোটাল</td>
                      <td className="text-right">৳{subtotal.toLocaleString()}</td>
                    </tr>
                    <tr className="subtotal-row">
                      <td>ডেলিভারি চার্জ</td>
                      <td className="text-right">৳{deliveryCharge}</td>
                    </tr>
                    <tr className="total-row">
                      <td>মোট পরিমাণ</td>
                      <td className="text-right">৳{total.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Payment Terms */}
              <div className="payment-terms">
                <div className="title">শর্তাবলী</div>
                <div className="content">
                  সকল পণ্য ডেলিভারির সময় চেক করে নিন। কোনো সমস্যা থাকলে ২৪ ঘন্টার মধ্যে জানান। 
                  পণ্য ফেরত দেওয়ার ক্ষেত্রে আমাদের নীতি অনুসরণ করুন।
                </div>
              </div>

              {/* Signature Area */}
              <div className="signature-area">
                <div className="signature-box">
                  <div className="signature-line">গ্রাহকের স্বাক্ষর</div>
                </div>
                <div className="signature-box">
                  <div className="signature-line">অনুমোদিত স্বাক্ষর</div>
                </div>
              </div>

              {/* Footer */}
              <div className="footer-section">
                গ্রামীণ বাজার - তাজা, সুস্বাদু ও নিরাপদ খাবারের নিশ্চয়তা
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Mock orders data - moved above component definition
const mockOrders = [
  {
    id: 'ORD-001',
    customerName: 'রহিম উদ্দিন',
    customerPhone: '০১৭১২৩৪৫৬৭৮',
    customerAddress: 'হাউস ১২৩, রোড ৪, ধানমন্ডি, ঢাকা',
    orderDate: '২০২৪-০১-১৫',
    status: 'pending',
    totalAmount: 850,
    paymentMethod: 'ক্যাশ অন ডেলিভারি',
    placedBySupport: false,
    items: [
      { name: 'টাজা টমেটো', quantity: 2, unit: 'কেজি', price: 120 },
      { name: 'দেশি পেঁয়াজ', quantity: 1, unit: 'কেজি', price: 80 },
      { name: 'বাসমতি চাল', quantity: 3, unit: 'কেজি', price: 180 }
    ]
  },
  {
    id: 'ORD-002',
    customerName: 'ফাতেমা খাতুন',
    customerPhone: '০১৮৯৮৭৬৫৪৩২',
    customerAddress: 'বাড়ি ৪৫, ব্লক সি, বসুন্ধরা আর/এ, ঢাকা',
    orderDate: '২০২৪-০১-১৪',
    status: 'confirmed',
    totalAmount: 1200,
    paymentMethod: 'বিকাশ',
    placedBySupport: true,
    supportAgent: 'সাকিব হাসান',
    items: [
      { name: 'মুরগির মাংস', quantity: 1, unit: 'কেজি', price: 350 },
      { name: 'গরুর দুধ', quantity: 2, unit: 'লিটার', price: 65 },
      { name: 'আম (হিমসাগর)', quantity: 3, unit: 'কেজি', price: 250 }
    ]
  },
  {
    id: 'ORD-003',
    customerName: 'করিম মিয়া',
    customerPhone: '০১৫৫৫১২৩৪৫৬',
    customerAddress: 'গ্রাম: কমলপুর, উপজেলা: সাভার, জেলা: ঢাকা',
    orderDate: '২০২৪-০১-১৩',
    status: 'processing',
    totalAmount: 650,
    paymentMethod: 'নগদ',
    placedBySupport: true,
    supportAgent: 'নাসিরা বেগম',
    items: [
      { name: 'দেশি পেঁয়াজ', quantity: 3, unit: 'কেজি', price: 80 },
      { name: 'টাজা টমেটো', quantity: 2, unit: 'কেজি', price: 120 },
      { name: 'গরুর দুধ', quantity: 1, unit: 'লিটার', price: 65 }
    ]
  },
  {
    id: 'ORD-004',
    customerName: 'আমিনা বেগম',
    customerPhone: '০১৭৭৭৮৮৮৯৯৯',
    customerAddress: 'ফ্ল্যাট ৭বি, বিল্ডিং ২, গুলশান ২, ঢাকা',
    orderDate: '২০২৪-০১-১২',
    status: 'shipped',
    totalAmount: 1450,
    paymentMethod: 'কার্ড পেমেন্ট',
    placedBySupport: false,
    items: [
      { name: 'বাসমতি চাল', quantity: 5, unit: 'কেজি', price: 180 },
      { name: 'মুরগির মাংস', quantity: 2, unit: 'কেজি', price: 350 }
    ]
  },
  {
    id: 'ORD-005',
    customerName: 'নাসির আহমেদ',
    customerPhone: '০১৬১২৩৪৫৬৭৮',
    customerAddress: 'রোড ১২, সেক্টর ৭, উত্তরা, ঢাকা',
    orderDate: '২০২৪-০১-১১',
    status: 'delivered',
    totalAmount: 920,
    paymentMethod: 'ক্যাশ অন ডেলিভারি',
    placedBySupport: false,
    items: [
      { name: 'আম (হিমসাগর)', quantity: 2, unit: 'কেজি', price: 250 },
      { name: 'গরুর দুধ', quantity: 3, unit: 'লিটার', price: 65 },
      { name: 'টাজা টমেটো', quantity: 1, unit: 'কেজি', price: 120 }
    ]
  }
];

const Orders = () => {
  const [orders, setOrders] = useState(mockOrders);
  const [filteredOrders, setFilteredOrders] = useState(mockOrders);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const orderStatuses = [
    { value: 'pending', label: 'অপেক্ষমান', icon: <PendingIcon />, color: 'warning' },
    { value: 'confirmed', label: 'নিশ্চিত', icon: <CheckCircleIcon />, color: 'success' },
    { value: 'processing', label: 'প্রসেসিং', icon: <OrderIcon />, color: 'info' },
    { value: 'shipped', label: 'শিপ করা হয়েছে', icon: <ShippingIcon />, color: 'secondary' },
    { value: 'delivered', label: 'ডেলিভারি সম্পন্ন', icon: <CheckCircleIcon />, color: 'success' },
    { value: 'cancelled', label: 'বাতিল', icon: <CancelIcon />, color: 'error' }
  ];

  const getStatusLabel = (status) => {
    const statusObj = orderStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getStatusIcon = (status) => {
    const statusObj = orderStatuses.find(s => s.value === status);
    return statusObj ? statusObj.icon : <OrderIcon />;
  };

  const getStatusColor = (status) => {
    const statusObj = orderStatuses.find(s => s.value === status);
    return statusObj ? statusObj.color : 'default';
  };

  // Filter orders based on search term, status, and date
  React.useEffect(() => {
    let filtered = orders;

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerPhone.includes(searchTerm)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (dateFilter) {
      filtered = filtered.filter(order => order.orderDate === dateFilter);
    }

    setFilteredOrders(filtered);
  }, [orders, searchTerm, statusFilter, dateFilter]);

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOpenOrderDialog(true);
  };

  const handleInvoiceClick = (order) => {
    setInvoiceOrder(order);
    setOpenInvoiceDialog(true);
  };

  const handleStatusUpdate = (orderId, newStatus) => {
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      )
    );
    
    setSnackbar({
      open: true,
      message: `অর্ডার ${orderId} এর স্ট্যাটাস ${getStatusLabel(newStatus)} এ আপডেট করা হয়েছে`,
      severity: 'success'
    });
    
    handleMenuClose();
  };

  const handleMenuOpen = (event, orderId) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrderId(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    if (newValue === 0) {
      setStatusFilter('');
    } else {
      const status = orderStatuses[newValue - 1]?.value;
      setStatusFilter(status);
    }
  };

  const calculateOrderStats = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayOrders = orders.filter(order => order.orderDate === today);
    const pendingOrders = orders.filter(order => order.status === 'pending');
    const totalRevenue = orders
      .filter(order => order.status === 'delivered')
      .reduce((sum, order) => sum + order.totalAmount, 0);

    return {
      totalOrders: orders.length,
      todayOrders: todayOrders.length,
      pendingOrders: pendingOrders.length,
      totalRevenue
    };
  };

  const stats = calculateOrderStats();

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
              <ShoppingCartIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                অর্ডার ব্যবস্থাপনা
              </Typography>
              <Typography variant="body1" color="text.secondary">
                আপনার দোকানের সকল অর্ডার দেখুন এবং পরিচালনা করুন
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              size="large"
            >
              প্রিন্ট রিপোর্ট
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              size="large"
            >
              এক্সপোর্ট করুন
            </Button>
          </Box>
        </Box>
      </StyledPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.totalOrders}
                  </Typography>
                  <Typography variant="body2">
                    মোট অর্ডার
                  </Typography>
                </Box>
                <OrderIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.todayOrders}
                  </Typography>
                  <Typography variant="body2">
                    আজকের অর্ডার
                  </Typography>
                </Box>
                <CalendarIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {stats.pendingOrders}
                  </Typography>
                  <Typography variant="body2">
                    অপেক্ষমান অর্ডার
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    ৳{stats.totalRevenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    মোট বিক্রয়
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <StyledPaper>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="অর্ডার আইডি, গ্রাহকের নাম বা ফোন নম্বর..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>অর্ডার স্ট্যাটাস</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="অর্ডার স্ট্যাটাস"
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="">সব স্ট্যাটাস</MenuItem>
                {orderStatuses.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {status.icon}
                      {status.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="তারিখ অনুযায়ী ফিল্টার"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              মোট: {filteredOrders.length}টি অর্ডার
            </Typography>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Orders Table */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            অর্ডার তালিকা
          </Typography>
          
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="সব অর্ডার" />
            {orderStatuses.map((status) => (
              <Tab key={status.value} label={status.label} />
            ))}
          </Tabs>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>অর্ডার আইডি</TableCell>
                <TableCell>গ্রাহক</TableCell>
                <TableCell>তারিখ</TableCell>
                <TableCell>স্ট্যাটাস</TableCell>
                <TableCell>পরিমাণ</TableCell>
                <TableCell>পেমেন্ট</TableCell>
                <TableCell align="center">অ্যাকশন</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOrderClick(order)}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {order.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.customerPhone}
                        </Typography>
                        {order.placedBySupport && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label="কাস্টমার সাপোর্ট"
                              size="small"
                              icon={<SupportAgentIcon />}
                              sx={{ 
                                bgcolor: '#e3f2fd',
                                color: '#1976d2',
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-icon': {
                                  fontSize: '12px'
                                }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{order.orderDate}</TableCell>
                    <TableCell>
                      <StatusChip
                        status={order.status}
                        label={getStatusLabel(order.status)}
                        icon={getStatusIcon(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        ৳{order.totalAmount}
                      </Typography>
                    </TableCell>
                    <TableCell>{order.paymentMethod}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="বিস্তারিত দেখুন">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOrderClick(order);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="ইনভয়েস প্রিন্ট">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInvoiceClick(order);
                            }}
                          >
                            <ReceiptIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="স্ট্যাটাস পরিবর্তন">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, order.id);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="প্রতি পৃষ্ঠায় অর্ডার:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} এর মধ্যে ${count !== -1 ? count : `${to} এর বেশি`}`
          }
        />
      </StyledPaper>

      {/* Order Details Dialog */}
      <Dialog
        open={openOrderDialog}
        onClose={() => setOpenOrderDialog(false)}
        maxWidth="md"
        fullWidth
        disableScrollLock={true}
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6">অর্ডার বিস্তারিত</Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    অর্ডার আইডি: {selectedOrder.id}
                  </Typography>
                </Box>
                <StatusChip
                  status={selectedOrder.status}
                  label={getStatusLabel(selectedOrder.status)}
                  icon={getStatusIcon(selectedOrder.status)}
                />
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Grid container spacing={3}>
                {/* Customer Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        গ্রাহকের তথ্য
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>নাম:</strong> {selectedOrder.customerName}
                        </Typography>
                      </Box>
                      
                      {selectedOrder.placedBySupport && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <SupportAgentIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                            সাপোর্ট এজেন্ট:
                          </Typography>
                          <Typography variant="body2">
                            {selectedOrder.supportAgent}
                          </Typography>
                          <Chip 
                            label="কাস্টমার সাপোর্ট দ্বারা স্থাপিত"
                            size="small"
                            color="info"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>ফোন:</strong> {selectedOrder.customerPhone}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <LocationIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>ঠিকানা:</strong> {selectedOrder.customerAddress}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Order Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OrderIcon color="primary" />
                        অর্ডার তথ্য
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>অর্ডারের তারিখ:</strong> {selectedOrder.orderDate}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>মোট পরিমাণ:</strong> ৳{selectedOrder.totalAmount}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          <strong>পেমেন্ট পদ্ধতি:</strong> {selectedOrder.paymentMethod}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Order Items */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        অর্ডারকৃত পণ্যসমূহ
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>পণ্যের নাম</TableCell>
                              <TableCell align="center">পরিমাণ</TableCell>
                              <TableCell align="center">একক দাম</TableCell>
                              <TableCell align="right">মোট দাম</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOrder.items.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>{item.name}</TableCell>
                                <TableCell align="center">{item.quantity} {item.unit}</TableCell>
                                <TableCell align="center">৳{item.price}</TableCell>
                                <TableCell align="right">৳{item.quantity * item.price}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                                মোট:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                ৳{selectedOrder.totalAmount}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setOpenOrderDialog(false)}>
                বন্ধ করুন
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReceiptIcon />}
                onClick={() => {
                  setOpenOrderDialog(false);
                  handleInvoiceClick(selectedOrder);
                }}
              >
                ইনভয়েস প্রিন্ট
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
              >
                স্ট্যাটাস আপডেট
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Status Change Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuProps={{ disableScrollLock: true }}
      >
        {orderStatuses.map((status) => (
          <MenuItem
            key={status.value}
            onClick={() => handleStatusUpdate(selectedOrderId, status.value)}
          >
            <ListItemIcon>
              {status.icon}
            </ListItemIcon>
            <ListItemText>
              {status.label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Invoice Dialog */}
      {openInvoiceDialog && (
        <InvoiceTemplate
          order={invoiceOrder}
          onClose={() => setOpenInvoiceDialog(false)}
        />
      )}
    </Container>
  );
};

export default Orders;
